import {
    AnonymousAddonGroup,
    DeviceType,
    IProduct, IProductCategory,
    IPurchase,
    IUser,
    PurchaseStatus,
    Status,
    UserPurchasing,
} from '@snackpass/snackpass-types';
import _ from 'lodash';
import { IScreen } from '../Redux';
import { getPurchaseStatus } from '../Utils/helpers';
import axios from './axios';

interface ScreenRegisterResponse {
    screen: IScreen;
}

export type GetMenuResponse = {
    products: Array<Omit<IProduct, "addonGroups"> & { addonGroups: string[] }>;
    addonGroups: Record<
        string,
        {
            id: string;
            ag: AnonymousAddonGroup;
            pRef: Record<string, { id: string; addons: string[] }>;
        }
    >;
    categories: IProductCategory[];
    cateringCategories: IProductCategory[];
};

/** MOCK */
const list: Partial<IPurchase>[] = [
    {
        _id: '1',
        user: { name: 'One One' } as UserPurchasing,
        receiptNumber: 12,
        transactionSource: 'kiosk',
        status: [{ type: PurchaseStatus.completed }] as Status[],
    },
    {
        _id: '2',
        user: { name: 'Two Two' } as UserPurchasing,
        receiptNumber: 10,
        transactionSource: 'kiosk',
        status: [{ type: PurchaseStatus.started }] as Status[],
    },
    {
        _id: '3',
        user: { name: 'Three Three' } as UserPurchasing,
        receiptNumber: 14,
        transactionSource: 'app',
        status: [{ type: PurchaseStatus.completed }] as Status[],
    },
    {
        _id: '4',
        user: { name: 'Four Four' } as UserPurchasing,
        receiptNumber: 15,
        transactionSource: 'app',
        status: [{ type: PurchaseStatus.completed }] as Status[],
    },
    {
        _id: '5',
        user: { name: 'Five Five' } as UserPurchasing,
        receiptNumber: 15,
        transactionSource: 'app',
        status: [{ type: PurchaseStatus.started }] as Status[],
    },
    {
        _id: '6',
        user: { name: 'Six Six' } as UserPurchasing,
        receiptNumber: 18,
        transactionSource: 'app',
        status: [{ type: PurchaseStatus.started }] as Status[],
    },
    {
        _id: '7',
        user: { name: 'Seven Seven' } as UserPurchasing,
        receiptNumber: 20,
        transactionSource: 'kiosk',
        status: [{ type: PurchaseStatus.started }] as Status[],
    },
    {
        _id: '8',
        user: { name: 'One One' } as UserPurchasing,
        receiptNumber: 12,
        transactionSource: 'kiosk',
        status: [{ type: PurchaseStatus.completed }] as Status[],
    },
    {
        _id: '9',
        user: { name: 'Two Two' } as UserPurchasing,
        receiptNumber: 10,
        transactionSource: 'kiosk',
        status: [{ type: PurchaseStatus.started }] as Status[],
    },
    {
        _id: '10',
        user: { name: 'Three Three' } as UserPurchasing,
        receiptNumber: 14,
        transactionSource: 'app',
        status: [{ type: PurchaseStatus.completed }] as Status[],
    },
    {
        _id: '11',
        user: { name: 'Four Four' } as UserPurchasing,
        receiptNumber: 15,
        transactionSource: 'app',
        status: [{ type: PurchaseStatus.completed }] as Status[],
    },
    {
        _id: '12',
        user: { name: 'Five Five' } as UserPurchasing,
        receiptNumber: 15,
        transactionSource: 'app',
        status: [{ type: PurchaseStatus.started }] as Status[],
    },
    {
        _id: '13',
        user: { name: 'Six Six' } as UserPurchasing,
        receiptNumber: 18,
        transactionSource: 'app',
        status: [{ type: PurchaseStatus.started }] as Status[],
    },
    {
        _id: '14',
        user: { name: 'Seven Seven' } as UserPurchasing,
        receiptNumber: 20,
        transactionSource: 'kiosk',
        status: [{ type: PurchaseStatus.started }] as Status[],
    },
];
/** MOCK */

const sleep = (milliseconds: number) => {
    return new Promise(resolve => setTimeout(resolve, milliseconds));
};

export const fetchStoreOrders = async () => {
    await sleep(700);

    let readyList = list.filter(
        purchase =>
            getPurchaseStatus(purchase as IPurchase) ===
            PurchaseStatus.completed
    );
    let inProgressList = list.filter(
        purchase =>
            getPurchaseStatus(purchase as IPurchase) === PurchaseStatus.started
    );
    inProgressList = _.sortBy(inProgressList, 'orderMethod');

    return [...readyList, ...inProgressList];
};

class Auth {
    static async sendVerification(phoneNumber: string) {
        return axios.post('phoneAuthentication/sendVerification', {
            phoneNumber,
        });
    }

    static async verify(phoneNumber: string, code: string, createUser = false) {
        return axios.post('phoneAuthentication/verifyPhone', {
            phoneNumber,
            code,
            createUser,
        });
    }
}

class StoreDevices {
    static getThis = async () => {
        const result = await axios.get<{ device: DeviceType; user: IUser }>(
            '/devices/store/this',
            {
                params: {
                    skipEsperLookups: true,
                },
            }
        );
        return result.data;
    };
}

class Users {
    static getMe = async () => await axios.get('/users/me');
    static exists = (email: string) =>
        axios.get('/users/exists', { params: { value: email } });
}

class Purchases {
    static get = async (params?: any) =>
        axios.get('/purchases', {
            params,
        });
}

class Stores {
    static getOne = (storeId: string) => axios.get(`/stores/${storeId}`);
    static getProducts = (storeId: string) =>
        axios.get<{ products: IProduct[] }>(`/stores/${storeId}/products`);
    static getMenu = (storeId: string) =>
        axios.get<GetMenuResponse>("/mul/menus/legacy", {
            params: {
                storeID: storeId,
                channel: "@snackpass/snacktv-web",
                includePayByWeight: true,
            }
        })
}

class SnackTV {
    static getScreenByDeviceId = (storeId: string, deviceId: string) =>
        axios.get<ScreenRegisterResponse>('/snackTV/by-device-id', {
            params: {
                storeId,
                deviceId,
            },
        });

    static update = (screenId: string, data: Partial<IScreen>) =>
        axios.put<ScreenRegisterResponse>(`/snackTV/${screenId}`, data);

    static ping = (deviceId: string) => axios.put(`/snackTV/${deviceId}/ping`);
}

export default class api {
    static auth = Auth;
    static devices = StoreDevices;
    static users = Users;
    static purchases = Purchases;
    static stores = Stores;
    static screens = SnackTV;
}
